// Compose-draft client hooks — server-saved new-message drafts shared with
// the team (list, edit, comment).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface ComposeDraftSummary {
  id: number;
  authorUserId: number;
  author: string;
  accountId: number | null;
  to: string[];
  subject: string;
  updatedAt: string;
  commentCount: number;
}

export interface ComposeDraftDetail {
  id: number;
  authorUserId: number;
  accountId: number | null;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
  /** Saved attachment chips (author's uploads, still valid server-side). */
  attachments?: { id: number; filename: string; mimeType: string; sizeBytes: number }[];
  updatedAt: string;
}

export interface DraftComment {
  id: number;
  authorUserId: number;
  author: string;
  body: string;
  createdAt: string;
}

export interface ComposeDraftPayload {
  accountId: number | null;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
  attachmentIds?: number[];
}

async function jfetch<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(url, { credentials: 'include', ...init });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error((j as { error?: string }).error || `HTTP ${r.status}`);
  }
  return r.json() as Promise<T>;
}

export function useComposeDrafts(enabled = true) {
  return useQuery<{ drafts: ComposeDraftSummary[] }>({
    queryKey: ['compose-drafts'],
    enabled,
    queryFn: () => jfetch('/api/compose-drafts'),
  });
}

export function fetchComposeDraft(id: number): Promise<ComposeDraftDetail> {
  return jfetch(`/api/compose-drafts/${id}`);
}

export function useSaveComposeDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: ComposeDraftPayload & { id?: number }) =>
      v.id
        ? jfetch<{ ok: boolean }>(`/api/compose-drafts/${v.id}`, {
            method: 'PUT',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(v),
          }).then(() => ({ id: v.id! }))
        : jfetch<{ id: number }>('/api/compose-drafts', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(v),
          }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['compose-drafts'] }),
  });
}

export function useDeleteComposeDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => jfetch(`/api/compose-drafts/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['compose-drafts'] }),
  });
}

export function useComposeDraftComments(draftId: number | null) {
  return useQuery<{ comments: DraftComment[] }>({
    queryKey: ['compose-draft-comments', draftId],
    enabled: draftId != null,
    refetchInterval: 20_000, // collaboration — pick up teammates' comments
    queryFn: () => jfetch(`/api/compose-drafts/${draftId}/comments`),
  });
}

export function useAddDraftComment(draftId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      jfetch(`/api/compose-drafts/${draftId}/comments`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ body }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['compose-draft-comments', draftId] });
      void qc.invalidateQueries({ queryKey: ['compose-drafts'] });
    },
  });
}

export function useDeleteDraftComment(draftId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) =>
      jfetch(`/api/compose-drafts/${draftId}/comments/${commentId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['compose-draft-comments', draftId] });
      void qc.invalidateQueries({ queryKey: ['compose-drafts'] });
    },
  });
}
